import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
	Steps,
	Button,
	Radio,
	Card,
	DatePicker,
	Input,
	InputNumber,
	Form,
	Space,
	Row,
	Col,
	Select,
	Tag,
	Descriptions,
	Divider,
	Empty,
} from 'antd';
import {
	ArrowLeftOutlined,
	CheckOutlined,
	CalendarOutlined,
	IdcardOutlined,
	AppstoreOutlined,
	RocketOutlined,
} from '@ant-design/icons';
import { getApi } from 'redux/sagas/getApiDataSaga';
import { postApi } from 'redux/sagas/postApiDataSaga';
import { SERVER_IP } from 'assets/Config';
import { resetApiStatus } from 'redux/reducers/globals/globalActions';
import { MOCK_WORKOUT_TEMPLATES } from 'data/workoutMockData';
import dayjs from 'dayjs';
import { useI18n } from 'hooks/useI18n';
import {
	getWorkoutGenderOptions,
	getWorkoutLevelOptions,
	translateWorkoutGender,
	translateWorkoutGoal,
	translateWorkoutLevel,
} from 'helpers/workoutI18n';
import './assign-plan.scss';

const LEVEL_COLORS = { Beginner: 'green', Intermediate: 'blue', Advanced: 'red' };

const AssignPlan = () => {
	const { customerId } = useParams();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { t } = useI18n();
	const globalRedux = useSelector((state) => state.globalRedux);
	const workoutTemplateRedux = useSelector((state) => state.workoutTemplateRedux);

	const [currentStep, setCurrentStep] = useState(0);
	const [source, setSource] = useState('template');
	const [selectedTemplate, setSelectedTemplate] = useState(null);
	const [genderFilter, setGenderFilter] = useState('');
	const [levelFilter, setLevelFilter] = useState('');
	const [form] = Form.useForm();
	const genderOptions = getWorkoutGenderOptions(t, 'workouts.enums.gender.all');
	const levelOptions = getWorkoutLevelOptions(t, 'workouts.enums.level.all');

	const templates = workoutTemplateRedux?.list?.length
		? workoutTemplateRedux.list
		: MOCK_WORKOUT_TEMPLATES;

	const filteredTemplates = templates.filter((t) => {
		const g = genderFilter ? t.gender === genderFilter : true;
		const l = levelFilter ? t.level === levelFilter : true;
		return g && l;
	});

	const fetchTemplates = useCallback(() => {
		dispatch(getApi('GET_WORKOUT_TEMPLATES', `${SERVER_IP}api/workoutTemplate`));
	}, [dispatch]);

	useEffect(() => {
		fetchTemplates();
	}, [fetchTemplates]);

	useEffect(() => {
		if (globalRedux.apiStatus.ADD_WORKOUT_PLAN === 'SUCCESS') {
			dispatch(resetApiStatus('ADD_WORKOUT_PLAN'));
			navigate(`/workout-plan/customer/${customerId}`);
		}
	}, [globalRedux.apiStatus.ADD_WORKOUT_PLAN, dispatch, navigate, customerId]);

	const handleSelectTemplate = (tpl) => {
		setSelectedTemplate(tpl);
		form.setFieldsValue({
			name: tpl.name,
			durationDays: tpl.durationDays,
		});
	};

	const handleAssign = async () => {
		try {
			const payload = {
				customer_id: customerId,
				template_id: selectedTemplate?.id,
				name: form.getFieldValue('name'),
				start_date: form.getFieldValue('startDate') ? dayjs(form.getFieldValue('startDate')).format('YYYY-MM-DD') : dayjs().add(1, 'day').format('YYYY-MM-DD'),
				duration_days: form.getFieldValue('durationDays'),
			};
			dispatch(postApi(payload, 'ADD_WORKOUT_PLAN', `${SERVER_IP}api/workoutPlan`));
		} catch {
			// form validation
		}
	};

	const assignmentValues = form.getFieldsValue();
	const selectedTemplateActiveDays = selectedTemplate?.days?.filter((day) => !day?.isRestDay).length || 0;
	const heroStats = [
		{
			icon: <IdcardOutlined />,
			tone: 'customer',
			label: t('workouts.plan.assign.stat_customer'),
			value: customerId.slice(0, 8),
			detail: t('workouts.plan.assign.customer_id'),
		},
		{
			icon: <AppstoreOutlined />,
			tone: 'templates',
			label: t('workouts.plan.assign.stat_templates'),
			value: filteredTemplates.length,
			detail: t('workouts.plan.assign.choose_source'),
		},
		{
			icon: <RocketOutlined />,
			tone: 'selected',
			label: t('workouts.plan.assign.stat_selected'),
			value: selectedTemplate?.name || t('workouts.plan.assign.none_selected'),
			detail: selectedTemplate ? t('workouts.common.days_count', { count: selectedTemplate.durationDays || 0 }) : t('workouts.plan.assign.none_selected_hint'),
		},
	];
	const confirmItems = [
		{ label: t('workouts.plan.assign.customer_id'), value: customerId },
		{ label: t('workouts.plan.assign.template'), value: selectedTemplate?.name || '-' },
		{ label: t('workouts.plan.assign.plan_name'), value: assignmentValues.name || '-' },
		{ label: t('workouts.plan.assign.start_date'), value: assignmentValues.startDate ? dayjs(assignmentValues.startDate).format('YYYY-MM-DD') : '-' },
		{ label: t('workouts.plan.assign.duration'), value: t('workouts.common.days_count', { count: assignmentValues.durationDays || 0 }) },
		{ label: t('workouts.plan.assign.stat_active_days'), value: selectedTemplateActiveDays || '-' },
	];

	const steps = [
		{
			title: t('workouts.plan.assign.choose_source'),
			content: (
				<div className="assign-plan-step">
					<div className="assign-plan-step__intro">
						<h3 className="assign-plan-step__title">{t('workouts.plan.assign.choose_source')}</h3>
						<p className="assign-plan-step__subtitle">{t('workouts.plan.assign.choose_source_subtitle')}</p>
					</div>
					<Radio.Group
						className="assign-plan-source-toggle"
						value={source}
						onChange={(e) => setSource(e.target.value)}
					>
						<Radio.Button value="template">{t('workouts.plan.assign.from_template')}</Radio.Button>
						<Radio.Button value="custom">{t('workouts.plan.assign.build_custom')}</Radio.Button>
					</Radio.Group>

					{source === 'template' && (
						<>
							<div className="assign-plan-filters">
							<Row gutter={[12, 12]}>
								<Col span={12}>
									<label className="assign-plan-filter-label" htmlFor="assign-plan-gender-filter">
										{t('workouts.form.fields.gender')}
									</label>
									<Select
										id="assign-plan-gender-filter"
										style={{ width: '100%' }}
										options={genderOptions}
										value={genderFilter}
										onChange={setGenderFilter}
										placeholder={t('workouts.form.placeholders.select_gender')}
									/>
								</Col>
								<Col span={12}>
									<label className="assign-plan-filter-label" htmlFor="assign-plan-level-filter">
										{t('workouts.plan.assign.fitness_level')}
									</label>
									<Select
										id="assign-plan-level-filter"
										style={{ width: '100%' }}
										options={levelOptions}
										value={levelFilter}
										onChange={setLevelFilter}
										placeholder={t('workouts.plan.assign.select_fitness_level')}
									/>
								</Col>
							</Row>
							</div>
							<Row gutter={[16, 16]} className="assign-plan-template-grid">
								{filteredTemplates.map((tpl) => {
									const isSelected = selectedTemplate?.id === tpl.id;

									return (
										<Col key={tpl.id} xs={24} sm={12}>
											<Card
												bordered={false}
												hoverable
												className={`assign-plan-template-card${isSelected ? ' is-selected' : ''}`}
												onClick={() => handleSelectTemplate(tpl)}
											>
												<div className="assign-plan-template-card__top">
													<div>
														<div className="assign-plan-template-card__title">{tpl.name}</div>
														<div className="assign-plan-template-card__meta">{t('workouts.common.days_count', { count: tpl.durationDays })}</div>
													</div>
													{isSelected && <span className="assign-plan-template-card__badge">Selected</span>}
												</div>
												<div className="assign-plan-template-card__tags">
													<Tag color={LEVEL_COLORS[tpl.level]}>{translateWorkoutLevel(t, tpl.level, tpl.level)}</Tag>
													<Tag>{translateWorkoutGender(t, tpl.gender, tpl.gender)}</Tag>
													<Tag>{translateWorkoutGoal(t, tpl.goal, tpl.goal)}</Tag>
												</div>
												<div className="assign-plan-template-card__footer">
													<span>{t('workouts.plan.assign.training_days_count', { count: tpl.days?.filter((day) => !day?.isRestDay).length || 0 })}</span>
													<span>{tpl.goal || t('workouts.plan.assign.general_goal')}</span>
												</div>
											</Card>
										</Col>
									);
								})}
								{filteredTemplates.length === 0 && (
									<Col span={24}>
										<Card bordered={false} className="assign-plan-empty-card">
											<Empty description={t('workouts.plan.assign.no_templates_match')} />
										</Card>
									</Col>
								)}
							</Row>
						</>
					)}

					{source === 'custom' && (
						<Card bordered={false} className="assign-plan-custom-card">
							<div className="assign-plan-custom-card__icon">
								<RocketOutlined />
							</div>
							<h4 className="assign-plan-custom-card__title">{t('workouts.plan.assign.custom_builder_title')}</h4>
							<p className="assign-plan-custom-card__copy">{t('workouts.plan.assign.custom_builder_copy')}</p>
							<Button
								type="primary"
								className="assign-plan-custom-card__action"
								onClick={openTemplateBuilder}
							>
								{t('workouts.plan.assign.open_template_builder')}
							</Button>
							<div className="assign-plan-custom-card__hint">{t('workouts.plan.assign.open_template_builder_hint')}</div>
						</Card>
					)}
				</div>
			),
		},
		{
			title: t('workouts.plan.assign.configure'),
			content: (
				<div className="assign-plan-step assign-plan-configure">
					<div className="assign-plan-step__intro">
						<h3 className="assign-plan-step__title">{t('workouts.plan.assign.configure')}</h3>
						<p className="assign-plan-step__subtitle">{t('workouts.plan.assign.configure_subtitle')}</p>
					</div>
					<Row gutter={[20, 20]}>
						<Col xs={24} lg={14}>
							<Form form={form} layout="vertical" className="assign-plan-form">
								<Form.Item name="name" label={t('workouts.plan.assign.plan_name')} rules={[{ required: true, message: t('forms.this_field_required') }]}>
									<Input placeholder={t('workouts.plan.assign.plan_name_placeholder')} />
								</Form.Item>
								<Form.Item
									name="startDate"
									label={t('workouts.plan.assign.start_date')}
									initialValue={dayjs().add(1, 'day')}
									rules={[{ required: true, message: t('forms.this_field_required') }]}
								>
									<DatePicker style={{ width: '100%' }} disabledDate={(d) => d && d < dayjs().startOf('day')} />
								</Form.Item>
								<Form.Item name="durationDays" label={t('workouts.plan.assign.duration_days')} rules={[{ required: true, message: t('forms.this_field_required') }]}>
									<InputNumber min={1} max={365} style={{ width: '100%' }} />
								</Form.Item>
							</Form>
						</Col>
						<Col xs={24} lg={10}>
							<Card bordered={false} className="assign-plan-preview-card">
								<div className="assign-plan-preview-card__title-wrap">
									<h4 className="assign-plan-preview-card__title">{selectedTemplate?.name || t('workouts.plan.assign.none_selected')}</h4>
									<div className="assign-plan-preview-card__sub">{t('workouts.plan.assign.preview_selected_program')}</div>
								</div>
								{selectedTemplate ? (
									<>
										<div className="assign-plan-preview-card__summary">
											<span><CalendarOutlined /> {t('workouts.common.days_count', { count: selectedTemplate.durationDays })}</span>
											<span>{t('workouts.plan.assign.active_days_count', { count: selectedTemplateActiveDays })}</span>
										</div>
										<Divider>{t('workouts.plan.assign.day_one_preview')}</Divider>
										<div className="assign-plan-preview-card__list">
											{selectedTemplate.days?.[0]?.items?.map((item, i) => (
												<div key={i} className="assign-plan-preview-card__item">
													<strong>{item.workoutName}</strong>
													<span>{item.sets} x {item.reps}</span>
												</div>
											))}
											{(!selectedTemplate.days?.[0]?.items?.length) && (
												<div className="assign-plan-preview-card__empty">{t('workouts.plan.assign.no_exercises_day_one')}</div>
											)}
										</div>
									</>
								) : (
									<Empty description={t('workouts.plan.assign.preview_empty')} />
								)}
							</Card>
						</Col>
					</Row>
				</div>
			),
		},
		{
			title: t('workouts.plan.assign.confirm'),
			content: (
				<Card bordered={false} className="assign-plan-confirm-card">
					<div className="assign-plan-step__intro">
						<h3 className="assign-plan-step__title">{t('workouts.plan.assign.confirm')}</h3>
						<p className="assign-plan-step__subtitle">{t('workouts.plan.assign.confirm_subtitle')}</p>
					</div>
					<div className="assign-plan-confirm-card__hero">
						<div>
							<div className="assign-plan-confirm-card__eyebrow">{t('workouts.plan.assign.template')}</div>
							<h4 className="assign-plan-confirm-card__title">{selectedTemplate?.name || t('workouts.plan.assign.none_selected')}</h4>
							<p className="assign-plan-confirm-card__copy">{t('workouts.plan.assign.confirm_summary_copy')}</p>
						</div>
						<div className="assign-plan-confirm-card__pill">
							<CalendarOutlined />
							<span>{t('workouts.common.days_count', { count: assignmentValues.durationDays || 0 })}</span>
						</div>
					</div>
					<div className="assign-plan-confirm-card__grid">
						{confirmItems.map((item) => (
							<div key={item.label} className="assign-plan-confirm-card__item">
								<span className="assign-plan-confirm-card__label">{item.label}</span>
								<strong className="assign-plan-confirm-card__value">{item.value}</strong>
							</div>
						))}
					</div>
				</Card>
			),
		},
	];

	const canGoNext = () => {
		if (currentStep === 0 && source === 'template' && !selectedTemplate) return false;
		return true;
	};

	function openTemplateBuilder() {
		const runtimeGlobal = Function('return this')();
		runtimeGlobal?.open?.('/workout-templates/create', '_blank');
	}

	return (
			<div className="assign-plan-page">
				<div className="assign-plan-shell">
					<div className="assign-plan-hero">
						<div className="assign-plan-hero__copy">
							<Button className="assign-plan-hero__back-btn" icon={<ArrowLeftOutlined />} onClick={() => navigate(`/workout-plan/customer/${customerId}`)}>
								{t('common.back')}
							</Button>
							<span className="assign-plan-hero__eyebrow">{t('workouts.plan.assign.hero_eyebrow')}</span>
							<h1 className="assign-plan-hero__title">{t('workouts.plan.assign.title')}</h1>
							<p className="assign-plan-hero__subtitle">{t('workouts.plan.assign.hero_subtitle')}</p>
						</div>
						<div className="assign-plan-hero__stats">
							{heroStats.map((item) => (
								<div
									key={item.label}
									className={`assign-plan-hero__stat assign-plan-hero__stat--${item.tone}${selectedTemplate && item.tone === 'selected' ? ' is-active' : ''}`}
								>
									<div className="assign-plan-hero__stat-head">
										<span className="assign-plan-hero__stat-icon">{item.icon}</span>
										<span className="assign-plan-hero__stat-label">{item.label}</span>
									</div>
									<strong className="assign-plan-hero__stat-value">{item.value}</strong>
									<span className="assign-plan-hero__stat-detail">{item.detail}</span>
								</div>
							))}
						</div>
					</div>

					<Card bordered={false} className="assign-plan-main-card">
						<Steps className="assign-plan-steps" current={currentStep} items={steps.map((s) => ({ title: s.title }))} />
						<div className="assign-plan-content">{steps[currentStep].content}</div>
						<div className="assign-plan-actions">
					{currentStep > 0 && (
						<Button onClick={() => setCurrentStep((s) => s - 1)}>{t('common.previous')}</Button>
					)}
					{currentStep < steps.length - 1 && (
						<Button type="primary" disabled={!canGoNext()} onClick={() => setCurrentStep((s) => s + 1)}>
							{t('common.next')}
						</Button>
					)}
					{currentStep === steps.length - 1 && (
						<Button
							type="primary"
							icon={<CheckOutlined />}
							loading={globalRedux.apiStatus.ADD_WORKOUT_PLAN === 'PENDING'}
							onClick={handleAssign}
						>
							{t('workouts.plan.assign.assign_plan')}
						</Button>
					)}
						</div>
					</Card>
				</div>
			</div>
	);
};

export default AssignPlan;
